import type * as Common from '@piccolohealth/pbs-common';
import type { SelectOption } from '@piccolohealth/ui';
import { ChakraV3 } from '@piccolohealth/ui';
import React from 'react';
import { useFormContext } from 'react-hook-form';
import { FaCheck } from 'react-icons/fa';
import { FormSection } from '../../../components/forms/FormSection';
import { FormStack } from '../../../components/forms/FormStack';
import { HookedAbcTemplates } from '../../../components/forms/HookedAbcTemplates';
import { HookedEpisodicSeverityScales } from '../../../components/forms/HookedEpisodicSeverityScales';
import { HookedFormField } from '../../../components/forms/HookedFormField';
import { HookedFormFieldset } from '../../../components/forms/HookedFormFieldset';
import { HookedInput } from '../../../components/forms/HookedInput';
import { HookedSelect } from '../../../components/forms/HookedSelect';
import { HookedSwitch } from '../../../components/forms/HookedSwitch';
import { HookedTextArea } from '../../../components/forms/HookedTextArea';
import { participantBehaviourStatusOptions } from './ParticipantBehaviourStatusTag';

export const ParticipantBehaviourForm = () => {
  const statusOptions: SelectOption<Common.ParticipantBehaviourStatus>[] = Object.values(
    participantBehaviourStatusOptions,
  );
  const { watch } = useFormContext();

  const isAbcEnabled = watch('methods.abc.enabled');
  const isFrequencyEnabled = watch('methods.frequency.enabled');
  const isDurationEnabled = watch('methods.duration.enabled');
  const isEpisodicSeverityEnabled = watch('methods.episodicSeverity.enabled');

  return (
    <ChakraV3.Tabs.Root
      orientation='vertical'
      variant='subtle'
      w='full'
      h='full'
      lazyMount
      unmountOnExit={false}
      defaultValue='general'
      size='sm'
    >
      <ChakraV3.Tabs.List>
        <ChakraV3.Tabs.Trigger value='general'>General</ChakraV3.Tabs.Trigger>
        <ChakraV3.Tabs.Trigger value='abc'>
          <ChakraV3.HStack w='full'>
            ABC
            <ChakraV3.Spacer />
            {isAbcEnabled && (
              <ChakraV3.Icon color='green.400'>
                <FaCheck />
              </ChakraV3.Icon>
            )}
          </ChakraV3.HStack>
        </ChakraV3.Tabs.Trigger>
        <ChakraV3.Tabs.Trigger value='frequency'>
          <ChakraV3.HStack w='full'>
            Frequency
            <ChakraV3.Spacer />
            {isFrequencyEnabled && (
              <ChakraV3.Icon color='green.400'>
                <FaCheck />
              </ChakraV3.Icon>
            )}
          </ChakraV3.HStack>
        </ChakraV3.Tabs.Trigger>
        <ChakraV3.Tabs.Trigger value='duration'>
          <ChakraV3.HStack w='full'>
            Duration
            <ChakraV3.Spacer />
            {isDurationEnabled && (
              <ChakraV3.Icon color='green.400'>
                <FaCheck />
              </ChakraV3.Icon>
            )}
          </ChakraV3.HStack>
        </ChakraV3.Tabs.Trigger>
        <ChakraV3.Tabs.Trigger value='episodicSeverity'>
          <ChakraV3.HStack w='full'>
            Severity
            <ChakraV3.Spacer />
            {isEpisodicSeverityEnabled && (
              <ChakraV3.Icon color='green.400'>
                <FaCheck />
              </ChakraV3.Icon>
            )}
          </ChakraV3.HStack>
        </ChakraV3.Tabs.Trigger>
      </ChakraV3.Tabs.List>
      <ChakraV3.Tabs.ContentGroup
        w='full'
        ml={6}
        pl={6}
        borderLeftWidth='1px'
        borderLeftColor='gray.200'
      >
        <ChakraV3.Tabs.Content value='general'>
          <FormSection heading='General' description='Basic information about a behaviour'>
            <ChakraV3.Separator />
            <FormStack>
              <HookedFormField name='name' label='Name' helperText='What is the behaviour called?'>
                <HookedInput name='name' placeholder='Enter a name..' />
              </HookedFormField>
              <HookedFormField name='status' label='Status' required>
                <HookedSelect name='status' size='sm' options={statusOptions} optionVariant='tag' />
              </HookedFormField>
              <HookedFormField
                name='description'
                label='Description'
                helperText='How would you describe the behaviour?'
              >
                <HookedTextArea
                  name='description'
                  placeholder='Enter a description..'
                  minH='4lh'
                />
              </HookedFormField>
              <HookedFormField
                name='example'
                label='Example'
                helperText='What is an example of the behaviour?'
              >
                <HookedTextArea name='example' placeholder='Enter an example..' minH='4lh' />
              </HookedFormField>
              <HookedFormField
                name='nonExample'
                label='Non-Example'
                helperText='What is a non-example of the behaviour?'
              >
                <HookedTextArea name='nonExample' placeholder='Enter a non-example..' minH='4lh' />
              </HookedFormField>
            </FormStack>
          </FormSection>
        </ChakraV3.Tabs.Content>
        <ChakraV3.Tabs.Content value='abc'>
          <FormSection heading='ABC' description='Antecedent, behaviour, consequence data'>
            <ChakraV3.Separator />
            <FormStack>
              <HookedFormField name='methods.abc' label='Enabled'>
                <HookedSwitch name={'methods.abc.enabled'} />
              </HookedFormField>
              <ChakraV3.Separator />
              <HookedFormField name='methods.templates.antecedent' label='Antecedents'>
                <HookedAbcTemplates name='methods.abc.templates.antecedent' />
              </HookedFormField>
              <HookedFormField name='methods.templates.behaviour' label='Behaviours'>
                <HookedAbcTemplates name='methods.abc.templates.behaviour' />
              </HookedFormField>
              <HookedFormField name='methods.templates.consequence' label='Consequences'>
                <HookedAbcTemplates name='methods.abc.templates.consequence' />
              </HookedFormField>
            </FormStack>
          </FormSection>
        </ChakraV3.Tabs.Content>
        <ChakraV3.Tabs.Content value='frequency'>
          <FormSection heading='Frequency' description='Number of times a behaviour occurs'>
            <ChakraV3.Separator />
            <FormStack>
              <HookedFormField name='methods.frequency' label='Enabled'>
                <HookedSwitch name={'methods.frequency.enabled'} />
              </HookedFormField>
            </FormStack>
          </FormSection>
        </ChakraV3.Tabs.Content>
        <ChakraV3.Tabs.Content value='duration'>
          <FormSection heading='Duration' description='How long a behaviour occurs for'>
            <ChakraV3.Separator />
            <FormStack>
              <HookedFormField name='methods.duration' label='Enabled'>
                <HookedSwitch name={'methods.duration.enabled'} />
              </HookedFormField>
            </FormStack>
          </FormSection>
        </ChakraV3.Tabs.Content>
        <ChakraV3.Tabs.Content value='episodicSeverity'>
          <FormSection heading='Episodic Severity' description='How severe a behaviour is'>
            <ChakraV3.Separator />
            <FormStack>
              <HookedFormField name='methods.episodicSeverity' label='Enabled'>
                <HookedSwitch name={'methods.episodicSeverity.enabled'} />
              </HookedFormField>
              <HookedFormFieldset name='methods.episodicSeverity.scales' label='Scales'>
                <HookedEpisodicSeverityScales name='methods.episodicSeverity.scales' />
              </HookedFormFieldset>
            </FormStack>
          </FormSection>
        </ChakraV3.Tabs.Content>
      </ChakraV3.Tabs.ContentGroup>
    </ChakraV3.Tabs.Root>
  );
};
